// Client brand registry. Maps a Sanity `client.name` to its public brand color
// and ink. When the Sanity record has no client name we treat it as a
// white-labeled engagement and use Axelerant tangerine.
//
// Colors below are sourced from each brand's public site / press kit and kept
// muted/dark enough that a paper-ink (white) logo treatment stays legible.

import { findNamedCase, type NamedCase } from "@/data/namedCases";
import { EXTRA_SCREENSHOTS } from "@/data/extraScreenshots";
import type { CaseLens, CasePlatform, CaseIndustry } from "@/data/caseStudies";
import redHatLogo from "@/assets/logos/red-hat.svg";
import axelerantWordmarkDark from "@/assets/axelerant-logo.png";


import stanfordLogo from "@/assets/logos/stanford-logo.svg";
import acquiaLogo from "@/assets/logos/platforms/acquia-logo.svg";
import wundermanLogo from "@/assets/logos/wunderman-thompson-logo.svg";
import advanceVelocityLogo from "@/assets/case-logos/advance-velocity-white.png.asset.json";
import mauticLogo from "@/assets/case-logos/mautic-white.png.asset.json";
import galtsgateLogo from "@/assets/case-logos/galtsgate-white.svg";
import configeroLogo from "@/assets/case-logos/configero-white.png.asset.json";
import eastWestCenterLogo from "@/assets/case-logos/east-west-center-white.png.asset.json";
import franklinMadisonLogo from "@/assets/case-logos/franklin-madison-white.png.asset.json";
import blueStateLogo from "@/assets/case-logos/blue-state-white.png.asset.json";
import castLogo from "@/assets/case-logos/cast-white.png.asset.json";
import facetLogo from "@/assets/case-logos/facet-white.png.asset.json";
import legacyLogo from "@/assets/case-logos/legacy-white.png.asset.json";
import lonelyPlanetLogo from "@/assets/case-logos/lonely-planet-white.png.asset.json";
import scoutsLogo from "@/assets/case-logos/scouts-white.png.asset.json";
import raceForwardMarkLogo from "@/assets/case-logos/race-forward-mark.png.asset.json";
import sunyLogo from "@/assets/case-logos/suny-white.png.asset.json";
import synduitLogo from "@/assets/case-logos/synduit-white.png.asset.json";
import wildstyleLogo from "@/assets/case-logos/wildstyle-network-white.png.asset.json";
import msfLogo from "@/assets/case-logos/doctors-without-borders-white.png.asset.json";
import unionCoopLogo from "@/assets/logos/union-coop-logo-2.svg";
import wrapLogo from "@/assets/logos/wrap-logo.png.asset.json";

export type ResolvedBrand = {
  client: string;
  isWhiteLabel: boolean;
  color: string;
  ink: "paper" | "ink";
  logo?: string;
  /** "silhouette" = recolor to paper/ink via CSS filter (local SVGs).
   *  "native"     = render the fetched logo directly on the brand ribbon. */
  logoTreatment?: "silhouette" | "native";
  /** Optional product/sub-line rendered under the logo on the card ribbon
   *  (used for Axelerant-internal cases to name the product). */
  subtitle?: string;
  industryHint?: string;
  /** For engagements delivered as a partner/subcontractor to a named agency: a
   *  subtle "In partnership with" credit rendered on the case hero + work card. */
  partner?: { name: string; logo?: string };
};



const AXELERANT_TANGERINE = "#F25A02";

// Slug set for Axelerant-internal engagements (products/tooling we built for
// ourselves). Sanity may have these mis-tagged against a client; we override
// here so the public case reads as an Axelerant internal record.
const AXELERANT_INTERNAL: Set<string> = new Set([
  "streamlining-recruitment-screening-process-with-aquiz",
]);

// Slug → product/sub-line rendered under the Axelerant logo on the ribbon.
const AXELERANT_INTERNAL_SUBTITLE: Record<string, string> = {
  "streamlining-recruitment-screening-process-with-aquiz": "AQuiz",
};


// Logo.dev publishable token (safe in browser per Logo.dev docs).
const LOGO_DEV_TOKEN = import.meta.env.VITE_LOVABLE_CONNECTOR_LOGO_DEV_API_KEY as
  | string
  | undefined;

function logoDevUrl(domain: string): string | undefined {
  if (!LOGO_DEV_TOKEN) return undefined;
  return `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=200&format=png&retina=true&fallback=404`;
}

// Lower-cased client name → { color, ink, logo?, domain? }. Add more as you confirm them.
// `domain` is used as a fallback to fetch a logo via Logo.dev when no local SVG is bundled.
const REGISTRY: Record<string, { color: string; ink: "paper" | "ink"; logo?: string; domain?: string; logoTreatment?: "silhouette" | "native" }> = {
  "red hat":                                   { color: "#EE0000", ink: "paper", logo: redHatLogo, domain: "redhat.com" },
  "acquia":                                    { color: "#2B85C7", ink: "paper", logo: acquiaLogo, domain: "acquia.com" },
  "stanford university":                       { color: "#8C1515", ink: "paper", logo: stanfordLogo, domain: "stanford.edu" },
  "ohchr":                                     { color: "#009EDB", ink: "paper", domain: "ohchr.org" },
  // Official mark used as-is (black polygon, white wordmark, grey offset
  // plates). Rendered natively on a light paper field so the black polygon
  // and its plates stay legible.
  "race forward":                              { color: "#F4F1EA", ink: "ink", logo: raceForwardMarkLogo.url, logoTreatment: "native" },
  "kohler":                                    { color: "#161616", ink: "paper", domain: "kohler.com" },
  "american medical association":              { color: "#5E2D87", ink: "paper", domain: "ama-assn.org" },
  "university of east london":                 { color: "#4DBCB0", ink: "paper", domain: "uel.ac.uk" },
  "internal displacement monitoring centre":   { color: "#018ECA", ink: "paper", domain: "internal-displacement.org" },
  "ironman":                                   { color: "#D71920", ink: "paper", domain: "ironman.com" },
  "energy safety canada":                      { color: "#0B3D67", ink: "paper", domain: "energysafetycanada.com" },
  "lonely planet global, inc":                 { color: "#01255C", ink: "paper", logo: lonelyPlanetLogo.url, domain: "lonelyplanet.com" },
  "blue state":                                { color: "#271897", ink: "paper", logo: blueStateLogo.url, domain: "bluestate.co" },
  "configero":                                 { color: "#7BD826", ink: "paper", logo: configeroLogo.url, domain: "configero.com" },
  "mautic community":                          { color: "#4E5E9E", ink: "paper", logo: mauticLogo.url, domain: "mautic.org" },
  "wunderman thompson":                        { color: "#0A1F3D", ink: "paper", logo: wundermanLogo, domain: "wundermanthompson.com" },
  "advance velocity":                          { color: "#012464", ink: "paper", logo: advanceVelocityLogo.url },
  "facet interactive":                         { color: "#1E1E1E", ink: "paper", logo: facetLogo.url, domain: "facetinteractive.com" },
  "synduit":                                   { color: "#FC920F", ink: "paper", logo: synduitLogo.url, domain: "synduit.com" },
  "wildstyle network":                         { color: "#1A1A1A", ink: "paper", logo: wildstyleLogo.url, domain: "wildstylenetwork.com" },
  "legacy.com":                                { color: "#151B24", ink: "paper", logo: legacyLogo.url, domain: "legacy.com" },
  "suny maritime college":                     { color: "#003366", ink: "paper", logo: sunyLogo.url, domain: "sunymaritime.edu" },
  "east-west center":                          { color: "#D21E26", ink: "paper", logo: eastWestCenterLogo.url, domain: "eastwestcenter.org" },
  "franklin-madison":                          { color: "#0045A6", ink: "paper", logo: franklinMadisonLogo.url, domain: "franklinmadison.com" },
  "world organization of the scout movement - wosm": { color: "#622599", ink: "paper", logo: scoutsLogo.url, domain: "scout.org" },
  "cast inc":                                  { color: "#301C5C", ink: "paper", logo: castLogo.url, domain: "cast.org" },
  "galt's gate":                               { color: "#2E5141", ink: "paper", logo: galtsgateLogo },
  "doctors without borders":                   { color: "#EE0000", ink: "paper", logo: msfLogo.url, domain: "doctorswithoutborders.org" },
  "médecins sans frontières":                  { color: "#EE0000", ink: "paper", logo: msfLogo.url, domain: "doctorswithoutborders.org" },
  "medecins sans frontieres":                  { color: "#EE0000", ink: "paper", logo: msfLogo.url, domain: "doctorswithoutborders.org" },
  "wrap":                                      { color: "#00563F", ink: "paper", logo: wrapLogo.url, domain: "wrap.ngo" },
};


function normalize(name?: string | null): string {
  return (name ?? "").trim().toLowerCase();
}

// Slug-keyed overrides for engagements Axelerant delivered as the technology
// execution partner to a named agency/consultancy. The public surface reads as
// a white-labeled Axelerant record; the partner is credited subtly.
const WHITE_LABEL_PARTNERSHIPS: Record<string, { name: string; logo?: string }> = {
  "exceptional-customer-support-through-salesforce-einstein-chatbot": {
    name: "Configero",
    logo: configeroLogo.url,
  },
  "salesforce-sf-chatbot-for-cx": {
    name: "Configero",
    logo: configeroLogo.url,
  },
  "streamlining-sales-and-support-systems-by-modernizing-legacy-salesforce-instance": {
    name: "Advance Velocity",
    logo: advanceVelocityLogo.url,
  },
  "optimizing-multisite-management-for-an-ecological-transformation-firm-acquia": {
    name: "Acquia",
    logo: acquiaLogo,
  },
};

// Slug-keyed overrides for engagements where the client is known but should be
// credited alongside a delivery partner (e.g. Axelerant delivered via Acquia).
// Unlike WHITE_LABEL_PARTNERSHIPS, this keeps the client-facing brand intact
// and only adds the partner credit.
const CASE_CLIENT_WITH_PARTNER: Record<
  string,
  { clientName: string; color: string; ink: "paper" | "ink"; logo: string; partner: { name: string; logo?: string } }
> = {
  "union-coop-digital-asset-transformation": {
    clientName: "Union Coop",
    color: "#1B75BB",
    ink: "paper",
    logo: unionCoopLogo,
    partner: { name: "Acquia", logo: acquiaLogo },
  },
};

/**
 * Resolve the brand surface for a case study. Order of precedence:
 * 1. NAMED_CASES (curated overrides with logos)
 * 2. WHITE_LABEL_PARTNERSHIPS (slug override)
 * 3. CLIENT_BRANDS registry by Sanity client.name
 * 4. White-labeled fallback (Axelerant tangerine)
 */
export function resolveCaseBrand(
  slug: string,
  sanityClientName?: string | null,
  headline?: string | null,
): ResolvedBrand {
  if (AXELERANT_INTERNAL.has(slug)) {
    return {
      client: "Axelerant",
      isWhiteLabel: false,
      color: AXELERANT_TANGERINE,
      ink: "paper",
      logo: axelerantWordmarkDark,
      logoTreatment: "silhouette",
      subtitle: AXELERANT_INTERNAL_SUBTITLE[slug],
    };
  }

  const clientPartner = CASE_CLIENT_WITH_PARTNER[slug];
  if (clientPartner) {
    return {
      client: clientPartner.clientName,
      isWhiteLabel: false,
      color: clientPartner.color,
      ink: clientPartner.ink,
      logo: clientPartner.logo,
      logoTreatment: "silhouette",
      partner: clientPartner.partner,
    };
  }
  const partnership = WHITE_LABEL_PARTNERSHIPS[slug];
  if (partnership) {
    return {
      client: "White-Labeled Engagement",
      isWhiteLabel: true,
      color: AXELERANT_TANGERINE,
      ink: "paper",
      partner: partnership,
    };
  }
  const named = findNamedCase(slug);
  if (named) {
    return {
      client: named.client,
      isWhiteLabel: false,
      color: named.brand.color,
      ink: named.brand.ink,
      logo: named.brand.logo,
      logoTreatment: named.brand.logo ? "silhouette" : undefined,
    };
  }
  const key = normalize(sanityClientName);
  if (key && REGISTRY[key]) {
    const r = REGISTRY[key];
    const localLogo = r.logo;
    const fetched = !localLogo && r.domain ? logoDevUrl(r.domain) : undefined;
    return {
      client: sanityClientName!,
      isWhiteLabel: false,
      color: r.color,
      ink: r.ink,
      logo: localLogo ?? fetched,
      logoTreatment: r.logoTreatment ?? (localLogo ? "silhouette" : fetched ? "native" : undefined),
    };
  }
  // Fallback: scan the headline for a small allow-list of unambiguous brand names.
  const hl = (headline ?? "").toLowerCase();
  const HEADLINE_SCAN: string[] = [
    "blue state",
    "lonely planet",
    "east-west center",
    "franklin-madison",
    "wunderman thompson",
    "advance velocity",
    "facet interactive",
    "suny maritime college",
    "world organization of the scout movement",
    "cast inc",
    "galt's gate",
    "mautic community",
    "doctors without borders",
    "médecins sans frontières",
    "medecins sans frontieres",
  ];
  if (hl) {
    for (const scan of HEADLINE_SCAN) {
      if (!hl.includes(scan)) continue;
      const r = REGISTRY[scan];
      if (!r) continue;
      const localLogo = r.logo;
      const fetched = !localLogo && r.domain ? logoDevUrl(r.domain) : undefined;
      const display = scan.replace(/\b\w/g, (c) => c.toUpperCase());
      return {
        client: display,
        isWhiteLabel: false,
        color: r.color,
        ink: r.ink,
        logo: localLogo ?? fetched,
        logoTreatment: r.logoTreatment ?? (localLogo ? "silhouette" : fetched ? "native" : undefined),
      };
    }
  }
  return {
    client: "White-Labeled Engagement",
    isWhiteLabel: true,
    color: AXELERANT_TANGERINE,
    ink: "paper",
  };
}

// Synthesize a NamedCase-shaped object so existing view code can render any
// Sanity-only case study without branching everywhere.
export function synthesizeNamedCase(
  slug: string,
  args: {
    sanityClientName?: string | null;
    sanitySlug?: string;
    headline?: string | null;
    summary?: string | null;
    industry?: string | null;
  },
): NamedCase {
  const existing = findNamedCase(slug);
  if (existing) return existing;
  const brand = resolveCaseBrand(slug, args.sanityClientName, args.headline);
  const industries = (args.industry ? [args.industry] : []) as CaseIndustry[];
  const screenshot = EXTRA_SCREENSHOTS[slug];
  return {
    slug,
    sanitySlug: args.sanitySlug,
    client: brand.client,
    headline: args.headline?.trim() || "Engagement record.",
    outcome: { primary: "Operational metric moved" },
    fallbackSituation:
      args.summary?.trim() ||
      "An engagement Axelerant ran end-to-end. The full record is below.",
    lenses: [] as CaseLens[],
    platforms: [] as CasePlatform[],
    industries,
    brand: {
      // logo may be undefined; view branches on hasLogo before rendering <img loading="lazy" decoding="async">.
      logo: brand.logo ?? "",
      color: brand.color,
      ink: brand.ink,
      logoTreatment: brand.logoTreatment,
      ...(brand.subtitle ? { subtitle: brand.subtitle } : {}),
      ...(brand.partner ? { partner: brand.partner } : {}),
    },

    ...(screenshot ? { siteScreenshot: screenshot } : {}),
  };
}
